// Display metadata for the self-service tiers (1 = Core, 2 = Premium,
// 3 = Enterprise). Prices are NOT hardcoded here — they come from the same
// place the in-app billing page gets them: GET /billing/plans, which lists
// the live Stripe prices by lookup key (core_monthly, premium_annually, ...).
//
// Feature bullets mirror the public pricing page (Public-Website/pricing).
//
// Allowance numbers are NOT hardcoded here either: each tier's `limits` lines
// are built from helpers/planAllowances.json, the client mirror of the
// server's PLAN_CATALOGUE (a server test fails if the two differ), so the
// plan cards can never advertise a figure the server does not apply. Only the
// wording lives here, and it matches the pills on the public pricing page.

import planAllowances from '../helpers/planAllowances.json'

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
]

const plural = (count, singular) => (count === 1 ? singular : `${singular}s`)

// One line per allowance the plan includes, every one marked with the
// asterisk that PLAN_ALLOWANCE_FOOTNOTE explains. A null (or zero) allowance
// means "not included in this plan", so it gets no line at all.
const limitsFor = (planKey) => {
  const allowances = planAllowances.plans?.[planKey]?.allowances ?? {}
  const included = (metric) =>
    typeof allowances[metric] === 'number' && allowances[metric] > 0
      ? allowances[metric]
      : null
  const figure = (count) => count.toLocaleString('en-GB')

  const employees = included('employees')
  const vehicles = included('vehicles')
  const baseLocations = included('baseLocations')
  const ptsJourneys = included('ptsJourneysPerMonth')
  const controlRoomHours = included('controlRoomHoursPerMonth')
  const concurrentRooms = included('concurrentControlRooms')

  return [
    employees &&
      `Up to ${figure(employees)} ${plural(employees, 'employee')}*`,
    vehicles && `${figure(vehicles)} ${plural(vehicles, 'vehicle')}*`,
    baseLocations &&
      `${figure(baseLocations)} ${plural(baseLocations, 'base location')}*`,
    ptsJourneys &&
      `Up to ${figure(ptsJourneys)} PTS ${plural(ptsJourneys, 'journey')} per calendar month*`,
    controlRoomHours &&
      `Up to ${figure(controlRoomHours)} active control-room ${plural(controlRoomHours, 'hour')} per calendar month*`,
    concurrentRooms &&
      (concurrentRooms === 1
        ? 'One active control room at a time*'
        : `Up to ${NUMBER_WORDS[concurrentRooms] ?? figure(concurrentRooms)} concurrently active ${plural(concurrentRooms, 'control room')}*`),
  ].filter(Boolean)
}

// Shown beneath the plan cards, word for word as on the public pricing page.
export const PLAN_ALLOWANCE_FOOTNOTE =
  "* Plan allowances apply. Employee, vehicle, base-location and concurrent control-room capacity may require an agreed upgrade before further records are added or further control rooms are opened. On Premium, control-room hours are a monthly limit: you're warned when 10 hours remain, and open control rooms close when they run out. On Enterprise, PTS journeys and control-room hours are recorded for review only and never automatically interrupt operational access or generate overage charges. Enterprise+ and personalised quotations are available for larger organisations."

export const TIER_CATALOGUE = [
  {
    key: 'core',
    name: 'Core',
    tier: 1,
    description: 'Core operations for smaller services.',
    limits: limitsFor('core'),
    features: [
      'Event planning — calendar, quotes, EMPs, staffing',
      'Shifts & rostering with marketplace and swaps',
      'ePCR — full clinical records, offline-first mobile',
      'Fleet & equipment — DVLA lookup, VDIs, defects',
      'Medicines management — custody chain, CD register',
      'Finance — invoicing, credit notes, Xero-ready export',
      'Mobile app for every crew member',
    ],
  },
  {
    key: 'premium',
    name: 'Premium',
    tier: 2,
    popular: true,
    description: 'Adds recruitment, deeper governance & CQC.',
    limits: limitsFor('premium'),
    features: [
      'Everything in Core, plus:',
      'Recruitment portal with automated reference chasing',
      'CQC Centre — readiness score & evidence packs',
      'Audits, risk register & governance meetings',
      'PGD library with electronic sign-off',
      'Make Ready — load lists, restock queue, pick lists',
      'Payroll, overtime claims & KPI dashboards',
      'Command & Control — control rooms, dispatch, treatment centres',
    ],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    tier: 3,
    description: 'The full platform for larger operations.',
    limits: limitsFor('enterprise'),
    features: [
      'Everything in Premium, plus:',
      'Patient Transport — bookings, hospital portal, tariffs, SLA packs',
      'Critical Care — live consoles, DOPS, procedure logbooks, drawn-up medicines',
      'A second concurrent control room and more control-room hours',
    ],
  },
]

const CURRENCY_SYMBOLS = {
  gbp: '£',
  eur: '€',
  usd: '$',
}

export const currencySymbol = (currency) =>
  CURRENCY_SYMBOLS[String(currency || '').toLowerCase()] ??
  String(currency || '').toUpperCase()

// plans: the price array returned by GET /billing/plans (res.data.data)
export const findPrice = (plans, tierKey, interval) =>
  (plans || []).find(
    (p) =>
      p.lookup_key === `${tierKey}_${interval}` &&
      p.recurring?.interval === (interval === 'annually' ? 'year' : 'month'),
  )

export const formatPrice = (price, interval) => {
  if (!price || typeof price.unit_amount !== 'number') return null
  const amount = price.unit_amount / 100
  const rounded =
    Number.isInteger(amount) ? amount.toLocaleString() : amount.toFixed(2)
  return {
    symbol: currencySymbol(price.currency),
    amount: rounded,
    per: interval === 'annually' ? 'year' : 'month',
  }
}
